/**
 * Firestore Service Layer
 *
 * Clean Code prensipleri:
 * - Single Responsibility: Her fonksiyon tek bir işi yapar
 * - DRY: Tekrar eden kod yok
 * - Error Handling: Tüm hatalar yakalanır ve anlamlı mesajlarla iletilir
 * - Type Safety: TypeScript strict mode ile tam tip güvenliği
 * - Input Validation: Tüm input'lar validate edilir (XSS/injection koruması)
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  query,
  orderBy,
  limit,
  Timestamp,
  DocumentData,
  QueryDocumentSnapshot,
} from 'firebase/firestore';
import { db, functions } from '../config/firebase';
import { httpsCallable } from 'firebase/functions';
import type {
  User,
  Conversation,
  Message,
  ApiError,
  Vehicle,
} from '../types';

// ==================== Helper Functions ====================

/**
 * Firestore document'ı type-safe şekilde dönüştürür
 */
function convertDocToData<T>(doc: QueryDocumentSnapshot<DocumentData>): T {
  return {
    id: doc.id,
    ...doc.data(),
  } as T;
}

/**
 * HTML/XSS injection koruması için string sanitization
 */
function sanitizeString(input: string): string {
  return input
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
}

/**
 * Hata mesajını kullanıcı dostu formata çevirir
 */
function createApiError(code: string, message: string, details?: unknown): ApiError {
  return { code, message, details };
}

// ==================== User Operations ====================

/**
 * Kullanıcı profili getirir
 * @param userId - Kullanıcı ID
 * @returns User object veya null
 */
export async function getUserProfile(userId: string): Promise<User | null> {
  try {
    if (!userId || userId.trim() === '') {
      throw createApiError('INVALID_USER_ID', 'Kullanıcı ID geçersiz');
    }

    const userDoc = await getDoc(doc(db, 'users', userId));

    if (!userDoc.exists()) {
      return null;
    }

    return {
      id: userDoc.id,
      ...userDoc.data(),
    } as User;
  } catch (error) {
    console.error('getUserProfile error:', error);
    throw error;
  }
}

/**
 * Yeni kullanıcı profili oluşturur
 * @param userId - Kullanıcı ID
 * @param email - Email adresi
 * @param displayName - Görünen isim (opsiyonel)
 * @param photoURL - Profil fotoğrafı URL (opsiyonel)
 * @param authProvider - Auth sağlayıcı ('google' | 'email')
 * @param isAnonymous - Anonymous kullanıcı mı (default: false)
 */
export async function createUserProfile(
  userId: string,
  email: string,
  displayName: string | null,
  photoURL: string | null,
  authProvider: 'google' | 'email' | 'apple',
  isAnonymous: boolean = false
): Promise<User> {
  try {
    // Input validation
    if (!userId) {
      throw createApiError('INVALID_USER_ID', 'Kullanıcı ID geçersiz');
    }

    // Email format validation (basic) - Allow empty email for anonymous users
    if (email && email.trim() !== '') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        throw createApiError('INVALID_EMAIL', 'Email formatı geçersiz');
      }
    }

    const now = Timestamp.now();
    const userData: Omit<User, 'id'> = {
      email,
      displayName: displayName ? sanitizeString(displayName) : null,
      photoURL,
      authProvider,
      userType: isAnonymous ? 'anonymous' : 'registered',
      createdAt: now,
      updatedAt: now,
      isPremium: false, // Default: free tier
      metadata: {
        totalConversations: 0,
        totalMessages: 0,
      },
      // Anonymous kullanıcılar için limitler ekle
      ...(isAnonymous && {
        limits: {
          maxConversations: 1,
          maxMessages: 3,
          maxVehicles: 1,
          usedConversations: 0,
          usedMessages: 0,
          usedVehicles: 0,
        },
      }),
    };

    await setDoc(doc(db, 'users', userId), userData);

    return {
      id: userId,
      ...userData,
    };
  } catch (error) {
    console.error('createUserProfile error:', error);
    throw error;
  }
}

// ==================== Conversation Operations ====================

/**
 * Kullanıcının tüm konuşmalarını getirir (en yeniden eskiye)
 * @param userId - Kullanıcı ID
 * @returns Conversation array
 */
export async function getUserConversations(
  userId: string
): Promise<Conversation[]> {
  try {
    if (!userId || userId.trim() === '') {
      throw createApiError('INVALID_USER_ID', 'Kullanıcı ID geçersiz');
    }

    const conversationsRef = collection(db, 'users', userId, 'conversations');
    const q = query(
      conversationsRef,
      orderBy('updatedAt', 'desc'),
      limit(50) // Max 50 konuşma (pagination için)
    );

    const snapshot = await getDocs(q);
    const allConversations = snapshot.docs.map((doc) =>
      convertDocToData<Conversation>(doc)
    );

    // isArchived alanı olmayan eski kayıtları da göster,
    // sadece açıkça true olanları gizle
    return allConversations.filter(
      (conversation) => (conversation as any).isArchived !== true
    );
  } catch (error) {
    console.error('getUserConversations error:', error);
    throw error;
  }
}

// NOTE: Conversation limit kontrolü backend'de yapılıyor (createConversation)
// Frontend'de limit kontrolü güvenlik riski oluşturur (client manipulation)
// Backend: functions/src/functions/createConversation.ts:136-169

// ==================== Message Operations ====================

/**
 * Belirli bir konuşmanın tüm mesajlarını getirir
 * @param userId - Kullanıcı ID
 * @param conversationId - Konuşma ID
 * @returns Message array
 */
export async function getConversationMessages(
  userId: string,
  conversationId: string
): Promise<Message[]> {
  try {
    if (!userId || !conversationId) {
      throw createApiError('INVALID_INPUT', 'Kullanıcı ID ve konuşma ID zorunludur');
    }

    const messagesRef = collection(
      db,
      'users',
      userId,
      'conversations',
      conversationId,
      'messages'
    );
    const q = query(messagesRef, orderBy('timestamp', 'asc'));

    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => convertDocToData<Message>(doc));
  } catch (error) {
    console.error('getConversationMessages error:', error);
    throw error;
  }
}

// ==================== Vehicle Operations ====================

/**
 * Kullanıcının kayıtlı araçlarını getirir
 * @param userId - Kullanıcı ID
 * @returns Vehicle array
 */
export async function getUserVehicles(userId: string): Promise<Vehicle[]> {
  try {
    if (!userId || userId.trim() === '') {
      throw createApiError('INVALID_USER_ID', 'Kullanıcı ID geçersiz');
    }

    const vehiclesRef = collection(db, 'users', userId, 'vehicles');
    const snapshot = await getDocs(vehiclesRef);
    return snapshot.docs.map((doc) => convertDocToData<Vehicle>(doc));
  } catch (error) {
    console.error('getUserVehicles error:', error);
    throw error;
  }
}

/**
 * Yeni araç kaydı oluşturur
 * @param userId - Kullanıcı ID
 * @param title - Araç başlığı
 * @param info - Araç detay bilgisi (max 100 karakter)
 */
export async function createUserVehicle(
  userId: string,
  title: string,
  info: string
): Promise<Vehicle> {
  try {
    const callable = httpsCallable<any, { id: string; title: string; info: string; createdAt: number; updatedAt: number }>(
      functions,
      'createVehicle'
    );
    const result = await callable({ title, info });
    const data = result.data;
    return {
      id: data.id,
      title: data.title,
      info: data.info,
      createdAt: Timestamp.fromMillis(data.createdAt),
      updatedAt: Timestamp.fromMillis(data.updatedAt),
    };
  } catch (error) {
    console.error('createUserVehicle error:', error);
    throw error;
  }
}

/**
 * Kullanıcının aracını günceller
 * @param userId - Kullanıcı ID
 * @param vehicleId - Güncellenecek araç ID
 * @param title - Yeni araç başlığı
 * @param info - Yeni araç detay bilgisi
 */
export async function updateUserVehicle(
  userId: string,
  vehicleId: string,
  title: string,
  info: string
): Promise<Vehicle> {
  try {
    if (!userId || userId.trim() === '') {
      throw createApiError('INVALID_USER_ID', 'Kullanıcı ID geçersiz');
    }

    if (!vehicleId || vehicleId.trim() === '') {
      throw createApiError('INVALID_VEHICLE_ID', 'Araç ID geçersiz');
    }

    const trimmedTitle = title.trim();
    const trimmedInfo = info.trim();

    if (!trimmedTitle) {
      throw createApiError('INVALID_TITLE', 'Araç başlığı zorunludur');
    }

    if (!trimmedInfo) {
      throw createApiError('INVALID_INFO', 'Araç bilgisi zorunludur');
    }

    if (trimmedInfo.length > 100) {
      throw createApiError('INFO_TOO_LONG', 'Araç bilgisi en fazla 100 karakter olabilir');
    }

    const vehicleRef = doc(db, 'users', userId, 'vehicles', vehicleId);
    const vehicleDoc = await getDoc(vehicleRef);

    if (!vehicleDoc.exists()) {
      throw createApiError('VEHICLE_NOT_FOUND', 'Araç bulunamadı');
    }

    const updateData = {
      title: sanitizeString(trimmedTitle),
      info: sanitizeString(trimmedInfo),
      updatedAt: Timestamp.now(),
    };

    await setDoc(vehicleRef, updateData, { merge: true });

    return {
      id: vehicleId,
      ...vehicleDoc.data(),
      ...updateData,
    } as Vehicle;
  } catch (error) {
    console.error('updateUserVehicle error:', error);
    throw error;
  }
}

/**
 * Kullanıcının aracını siler
 * @param userId - Kullanıcı ID
 * @param vehicleId - Silinecek araç ID
 */
export async function deleteUserVehicle(
  userId: string,
  vehicleId: string
): Promise<void> {
  try {
    if (!userId || userId.trim() === '') {
      throw createApiError('INVALID_USER_ID', 'Kullanıcı ID geçersiz');
    }

    if (!vehicleId || vehicleId.trim() === '') {
      throw createApiError('INVALID_VEHICLE_ID', 'Araç ID geçersiz');
    }

    const vehicleRef = doc(db, 'users', userId, 'vehicles', vehicleId);
    await deleteDoc(vehicleRef);
  } catch (error) {
    console.error('deleteUserVehicle error:', error);
    throw error;
  }
}

// ==================== Config Operations ====================

// NOTE: AI settings (betaAiSettings) backend-only
// Frontend CANNOT read config/betaAiSettings for security (SEC-001)
// All AI configuration managed by backend (Cloud Functions)
// Backend: functions/src/flows/chatFlow.ts:140

// ==================== Validation Utilities ====================

/**
 * Mesaj içeriğini validate eder (uzunluk, içerik vb.)
 * @param message - Mesaj içeriği
 * @returns Validation sonucu
 */
export function validateMessage(message: string): {
  isValid: boolean;
  error?: string;
} {
  // Boş mesaj kontrolü
  if (!message || message.trim() === '') {
    return {
      isValid: false,
      error: 'Mesaj boş olamaz',
    };
  }

  // Uzunluk kontrolü (max 2000 karakter)
  if (message.length > 2000) {
    return {
      isValid: false,
      error: 'Mesaj en fazla 2000 karakter olabilir',
    };
  }

  // Sadece boşluk karakteri kontrolü
  if (message.trim().length === 0) {
    return {
      isValid: false,
      error: 'Mesaj sadece boşluk içeremez',
    };
  }

  return { isValid: true };
}

/**
 * Konuşmayı siler (tüm mesajlarıyla birlikte)
 * @param userId - Kullanıcı ID
 * @param conversationId - Konuşma ID
 */
export async function deleteConversation(
  userId: string,
  conversationId: string
): Promise<void> {
  try {
    if (!userId || userId.trim() === '') {
      throw createApiError('INVALID_USER_ID', 'Kullanıcı ID geçersiz');
    }

    if (!conversationId || conversationId.trim() === '') {
      throw createApiError('INVALID_CONVERSATION_ID', 'Konuşma ID geçersiz');
    }

    const conversationRef = doc(
      db,
      'users',
      userId,
      'conversations',
      conversationId
    );

    // Önce tüm mesajları sil
    const messagesRef = collection(conversationRef, 'messages');
    const messagesSnapshot = await getDocs(messagesRef);

    const deletePromises = messagesSnapshot.docs.map((messageDoc) =>
      deleteDoc(messageDoc.ref)
    );

    await Promise.all(deletePromises);

    // Sonra konuşmayı sil
    await deleteDoc(conversationRef);
  } catch (error) {
    console.error('deleteConversation error:', error);
    throw error;
  }
}

/**
 * Yeni bir konuşma başlatır (AI çağırmadan sadece veritabanı kaydı)
 * @param userId - Kullanıcı ID
 * @param vehicleId - Araç ID (opsiyonel)
 * @param vehicleTitle - Araç başlığı (opsiyonel)
 * @param vehicleInfo - Araç bilgisi (opsiyonel)
 * @param moduleId - Modül ID (default: 'automotive')
 */
export async function initializeConversation(
  userId: string,
  vehicleId?: string,
  vehicleTitle?: string,
  vehicleInfo?: string,
  moduleId: string = 'automotive'
): Promise<string> {
  try {
    if (!userId) {
      throw createApiError('INVALID_USER_ID', 'Kullanıcı ID geçersiz');
    }

    const now = Timestamp.now();
    const conversationsRef = collection(db, 'users', userId, 'conversations');
    const conversationDoc = doc(conversationsRef);

    const conversationData: any = {
      userId,
      moduleId,
      createdAt: now,
      updatedAt: now,
      messageCount: 0,
      isArchived: false,
    };

    if (vehicleId) conversationData.vehicleId = vehicleId;
    if (vehicleTitle) conversationData.vehicleTitle = sanitizeString(vehicleTitle);
    if (vehicleInfo) conversationData.vehicleInfo = sanitizeString(vehicleInfo);

    await setDoc(conversationDoc, conversationData);

    return conversationDoc.id;
  } catch (error) {
    console.error('initializeConversation error:', error);
    throw error;
  }
}
